
import React, { useState } from 'react';
import AnimatedSection from './AnimatedSection';
import SectionHeading from './SectionHeading';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

const experiences = [
  {
    company: 'Johnson Controls',
    role: 'Platform Engineer',
    date: 'May 2023 – Present',
    points: [
      'Architected multi-cloud Kubernetes platforms across AWS, Azure, GCP, and on-prem environments with 99.9%+ availability.',
      'Built a Kubernetes-native CI/CD platform with Tekton, ArgoCD, Buildpacks, and GitOps for 50+ engineers.',
      'Created canary releases, health checks, automated rollback, and Git-based monorepo change detection, reducing release cycles from hours to under 15 minutes.',
      'Built a Platform Operations Assistant with LangGraph and Azure OpenAI for natural-language CI/CD investigations.',
      'Designed stateful agent workflows with retrieval, SonarQube analysis, PipelineRun lookup, checkpoint recovery, caching, and concurrency controls.',
      'Owned 20+ P0/P1 incidents and reduced MTTR by 40% through deep Linux, Kubernetes, and application-level analysis.',
      'Defined SLOs, SLIs, and error budgets with Prometheus, Grafana, Loki, and Alertmanager, cutting alert noise by 60%.',
      'Delivered 25–30% cloud cost savings with Terraform, Ansible, rightsizing, lifecycle automation, and infrastructure optimization.'
    ]
  },
  {
    company: 'Virtusa',
    role: 'Software Engineering Intern',
    date: 'Jan 2023 – May 2023',
    points: [
      'Developed and maintained Java Spring Boot microservices with efficient JDBC data access layers.',
      'Containerized applications with Docker and improved responsiveness through asynchronous AJAX communication.'
    ]
  }
];

export default function Experience() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <AnimatedSection id="experience">
      <SectionHeading number={2}>Where I've Worked</SectionHeading>
      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex md:flex-col overflow-x-auto md:overflow-x-visible">
          {experiences.map((exp, index) => (
            <button
              key={exp.company}
              onClick={() => setActiveTab(index)}
              className={`font-mono text-sm px-4 py-3 text-left border-l-2 whitespace-nowrap transition-all duration-300 ${
                activeTab === index 
                  ? 'border-teal-300 text-teal-300'
                  : 'border-slate-700 text-slate-400 hover:text-teal-300'
              }`}
            style={activeTab === index ? { background:'rgba(45,212,191,0.07)' } : {}}
            >
              {exp.company}
            </button>
          ))}
        </div>
        <div className="flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-xl font-medium text-slate-100">
                {experiences[activeTab].role} <span className="text-teal-300">@ {experiences[activeTab].company}</span>
              </h3>
              <p className="font-mono text-sm text-slate-500 mt-1 mb-6">{experiences[activeTab].date}</p>
              <ul className="space-y-3">
                {experiences[activeTab].points.map((point, i) => (
                  <li key={i} className="flex gap-3">
                    <ChevronRight className="text-teal-300 w-5 h-5 mt-1 flex-shrink-0" />
                    <span className="text-slate-400">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </AnimatedSection>
  );
}
